import {
  DraftRunStatus,
  IDraftMessage,
  IDraftRecommendation,
  IDraftRunVersion,
} from "../../../store/queries/contractModelDrafts";
import { isProcessing, openQuestionsOf } from "../utils";
import { WIZARD_STEPS } from "./steps";

/**
 * CLE-2811 — the run as ONE conversation. Pure helpers, so the ordering rules are tested without a DOM.
 */

export type IThreadItem =
  | { kind: "version"; key: string; versionNo: number; createdAt: string }
  /**
   * CLE-2811 — the run instructions she gave before the first generation, shown as her first message.
   * Synthetic (no message row behind it), so it is never queued and never deletable.
   */
  | { kind: "instructions"; key: string; body: string }
  | {
      kind: "message";
      key: string;
      message: IDraftMessage;
      /** Her message, not yet consumed by any pass. Only these can be deleted. */
      queued: boolean;
      /** Her optimistic message, before the server has echoed it back. */
      sending?: boolean;
    };

export const isAssistantMessage = (m: IDraftMessage): boolean =>
  m.role === "assistant";

export const isQueuedMessage = (m: IDraftMessage): boolean =>
  !isAssistantMessage(m) && !m.appliedInVersionId;

const time = (iso: string): number => new Date(iso).getTime();

/** The stable key of the run-instructions bubble: one per run, never a message id. */
export const instructionsKey = (runId: string): string =>
  `instructions:${runId}`;

/**
 * Messages and version boundaries in one chronological thread. A version separator sits at the moment
 * that pass was started, so the thread reads as "what she asked → Version N → what the agent answered".
 * Version 1 is left out: it is created with the run, before anything is said, and a separator at the
 * very top of the thread says nothing. On a timestamp tie the separator goes first — a pass's own
 * reply can never precede the pass.
 *
 * CLE-2811 — the run's instructions (`generationInstructions`), when there are any, open the thread as
 * her first bubble: they are what she said before the first generation, so they precede every message
 * and every separator. They used to sit in a card above the wizard, apart from the conversation.
 */
export const buildThread = (
  messages: IDraftMessage[],
  versions: IDraftRunVersion[],
  instructions?: { runId: string; body: string | null }
): IThreadItem[] => {
  const items: { at: number; order: number; item: IThreadItem }[] = [];
  versions
    .filter((v) => v.versionNo > 1)
    .forEach((v) =>
      items.push({
        at: time(v.createdAt),
        order: 0,
        item: {
          kind: "version",
          key: `version-${v.id}`,
          versionNo: v.versionNo,
          createdAt: v.createdAt,
        },
      })
    );
  messages.forEach((m) =>
    items.push({
      at: time(m.createdAt),
      order: 1,
      item: {
        kind: "message",
        key: m.id,
        message: m,
        queued: isQueuedMessage(m),
      },
    })
  );
  // Array.prototype.sort is stable, so equal (at, order) keep their server order.
  const thread = items
    .sort((a, b) => a.at - b.at || a.order - b.order)
    .map((x) => x.item);
  const body = instructions?.body?.trim();
  if (!instructions || !body) return thread;
  return [
    { kind: "instructions", key: instructionsKey(instructions.runId), body },
    ...thread,
  ];
};

/**
 * Every distinct open question on the current version, in recommendation order. A withdrawn
 * (`superseded`) or rejected recommendation's questions are moot — nobody is building it.
 */
export const collectOpenQuestions = (
  recs: IDraftRecommendation[]
): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  recs
    .filter((rec) => rec.status !== "superseded" && rec.status !== "rejected")
    .forEach((rec) =>
      openQuestionsOf(rec).forEach((q) => {
        if (!seen.has(q)) {
          seen.add(q);
          out.push(q);
        }
      })
    );
  return out;
};

// ---------------------------------------------------------------------------------------------
// Schedule blocks — a rate list inside a reply
// ---------------------------------------------------------------------------------------------

/**
 * One schedule row: a code, then an amount — "27130 14657", "G0105 $250.00", "0360: 1,234.50",
 * "27447-RT — 12,000", "J1885 150%". The code is a CPT / HCPCS / revenue code (3–5 letters or digits,
 * optionally a modifier); the amount may carry a "$", thousands commas, decimals or a "%".
 */
const SCHEDULE_ROW =
  /^\s*[A-Z0-9]{3,5}(?:-[A-Z0-9]{2})?\s*(?:[:=|–—-]\s*|\s)\s*\$?\d[\d,]*(?:\.\d+)?\s*%?\s*$/i;

/** At least this many consecutive rows make a schedule block; fewer read as ordinary text. */
export const SCHEDULE_MIN_ROWS = 3;

export const isScheduleRow = (line: string): boolean => SCHEDULE_ROW.test(line);

export interface ITextSegment {
  kind: "text" | "schedule";
  text: string;
}

/**
 * CLE-2811 — a reply can carry a whole rate list ("List every CPT code and rate, one per line"). Runs of
 * SCHEDULE_MIN_ROWS or more consecutive "<code> <amount>" lines become a `schedule` segment, shown in a
 * monospace block so the columns line up; everything else stays `text`. Two rows are just a sentence's
 * worth and stay text. Joining the segments' text with "\n" gives back the input.
 */
export const splitScheduleBlocks = (text: string): ITextSegment[] => {
  const lines = text.split("\n");
  const out: ITextSegment[] = [];
  let plain: string[] = [];
  const flushPlain = () => {
    if (plain.length) out.push({ kind: "text", text: plain.join("\n") });
    plain = [];
  };
  let i = 0;
  while (i < lines.length) {
    let j = i;
    while (j < lines.length && isScheduleRow(lines[j])) j += 1;
    if (j - i >= SCHEDULE_MIN_ROWS) {
      flushPlain();
      out.push({ kind: "schedule", text: lines.slice(i, j).join("\n") });
      i = j;
    } else {
      plain.push(lines[i]);
      i += 1;
    }
  }
  flushPlain();
  return out;
};

// ---------------------------------------------------------------------------------------------
// The live reply — the AI's words while a pass runs
// ---------------------------------------------------------------------------------------------

/** The narration the live reply bubble shows, remembered for the pass it belongs to. */
export interface ILiveReplyMemory {
  /** The version the text belongs to; null when nothing is remembered. */
  versionNo: number | null;
  text: string;
}

export const EMPTY_LIVE_REPLY: ILiveReplyMemory = { versionNo: null, text: "" };

/**
 * CLE-2811 — while a pass runs, the thread's last item is the AI's reply as it is being written: the
 * worker's streamed `progress.narration`. The worker clears its progress when it deposits, so for the few
 * seconds of `worker_returned` the last words seen are kept rather than falling back to "Thinking…".
 * Forgotten as soon as the run is not generating, or a new version starts (a chained pass), so one pass's
 * words never appear under the next.
 */
export const rememberNarration = (
  prev: ILiveReplyMemory,
  run: {
    status: DraftRunStatus;
    versionNo: number;
    progress: { narration?: string | null } | null;
  }
): ILiveReplyMemory => {
  if (!isProcessing(run.status)) return EMPTY_LIVE_REPLY;
  const narration =
    typeof run.progress?.narration === "string" ? run.progress.narration : "";
  if (narration.trim())
    return prev.versionNo === run.versionNo && prev.text === narration
      ? prev
      : { versionNo: run.versionNo, text: narration };
  return prev.versionNo === run.versionNo
    ? prev
    : { versionNo: run.versionNo, text: "" };
};

/** "Contract Terms" for `terms`; null for no step or one the wizard no longer has. */
export const stepTitle = (stepId: string | null): string | null =>
  stepId ? WIZARD_STEPS.find((s) => s.id === stepId)?.title ?? null : null;

/** "15:20" — the thread is read on the day it is written; the date would be noise. */
export const formatThreadTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
